import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { PromoBanner } from '@models';
import { AccessibleClickDirective } from '@shared';
import Swiper from 'swiper';
import { BreakpointObserverService } from '@core';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'lib-promo-banner',
  templateUrl: './promo-banner.component.html',
  styleUrl: './promo-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, AccessibleClickDirective, RouterModule],
})
export class PromoBannerComponent {
  isMobile = inject(BreakpointObserverService).isMobile;

  swiperContainerRef = viewChild<ElementRef>('swiperPromoContainer');

  slides = input<PromoBanner[]>([]);

  swiper: Swiper | null = null;

  isAutoplayEnabled = signal(true);
  activeIndex = signal(0);
  isTwoOrMoreSlides = computed(() => (this.slides()?.length ?? 0) > 1);

  constructor() {
    effect(() => {
      this.slides();
      this.initSwiper();
    });
  }

  nextSlide(): void {
    this.swiper?.slideNext();
  }

  prevSlide(): void {
    this.swiper?.slidePrev();
  }

  toggleAutoPlay(): void {
    this.isAutoplayEnabled.update((value) => !value);
    this.isAutoplayEnabled() ? this.swiper?.autoplay.start() : this.swiper?.autoplay.stop();
  }

  private updateActiveIndex(swiper: Swiper): void {
    this.activeIndex.set(swiper?.realIndex ?? 0);
  }

  private initSwiper(): void {
    // Destroy existing Swiper instance if it exists
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.swiper = new Swiper(this.swiperContainerRef()!.nativeElement, {
      loop: this.isTwoOrMoreSlides(),
      autoplay: this.isTwoOrMoreSlides()
        ? {
            delay: 10000,
            disableOnInteraction: false,
          }
        : false,
      keyboard: { enabled: false },
      slidesPerView: 1,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      on: {
        slideChange: (swiper: Swiper) => {
          this.updateActiveIndex(swiper);
        },
      },
    });
  }
}
